import {
  ExpandLessRounded,
  ExpandMoreRounded,
  OpenInNew,
} from '@mui/icons-material'
import {
  alpha,
  Box,
  Collapse,
  darken,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import MarketingLink from '~/components/marketingLink'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import {
  ESettingsStepFriendlyNames,
  pathByFriendlyStepName,
} from '~/features/settings/models'
import EHeyLibbyAdminNavItemNames from '~/layouts/account/constants/navItems'
import { SIDE_NAV_HEADER_CLASS_NAME } from '~/layouts/account/header/nav/side/constants'
import {
  ENavItemNames,
  TNavItem,
  TNavItemChild,
} from '~/layouts/account/models'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import { store } from '~/store'
import rem from '~/utils/ui'

type TProps = {
  navItem: TNavItem
  close: () => void
}

function NavItem({ navItem, close }: TProps) {
  const [setUIElements, sideNavOpenSections] = store((state) => [
    state.setUIElements,
    state.UIElements.sideNavOpenSections,
  ])
  const theme = useTheme()
  const isPartner = useIsPartner()

  const [siteMetadataNavAutoOpened, setSiteMetadataNavAutoOpened] =
    useState(false)
  const collapseTriggerRef = useRef(null)
  const { pathname } = useLocation()

  const isChildNavActive = (child: TNavItemChild) => {
    if ('path' in child && child.path && pathname.includes(child.path)) {
      return true
    }
    return (
      child.children?.some(
        (subChild) =>
          subChild.path === pathname ||
          (subChild.path && pathname.startsWith(`${subChild.path}/`)),
      ) ?? false
    )
  }

  const handleCollapseClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    if (!navItem.children?.length) {
      return
    }
    if (
      collapseTriggerRef.current &&
      (collapseTriggerRef.current as HTMLElement).contains(event.target as Node)
    ) {
      setUIElements({
        sideNavOpenSections: {
          ...sideNavOpenSections,
          [navItem.name]: !sideNavOpenSections[navItem.name],
        },
      })

      event.preventDefault()
      event.stopPropagation()
    }
  }

  const isOnAdminPortalConfigRoute =
    pathname.includes(EAdminPortalClientPath.portalBranding) ||
    pathname.includes(EAdminPortalClientPath.portalGeneralSettings)

  const getCollapsedState = (navigationItem: TNavItem) => {
    if (navigationItem.name === EHeyLibbyAdminNavItemNames.siteMetadata) {
      const isSiteMetadataOpen =
        !!sideNavOpenSections[EHeyLibbyAdminNavItemNames.siteMetadata]
      if (
        isOnAdminPortalConfigRoute &&
        !isSiteMetadataOpen &&
        !siteMetadataNavAutoOpened
      ) {
        setSiteMetadataNavAutoOpened(true)
        return true
      }
      return isSiteMetadataOpen
    }

    return !!sideNavOpenSections[navigationItem.name]
  }

  if (navItem.isSectionHeader) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        className={SIDE_NAV_HEADER_CLASS_NAME}
        sx={{
          pt: rem(16),
          pb: rem(10),
          px: rem(8),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            fontSize: rem(11),
            letterSpacing: rem(2),
            color: alpha(theme.palette.menuFont, 0.7),
            textTransform: 'uppercase',
          }}
        >
          {navItem.name}
        </Typography>
        <Divider
          sx={{
            flexGrow: 1,
            backgroundColor: alpha(theme.palette.menuFont, 0.2),
            marginX: rem(8),
          }}
        />
      </Stack>
    )
  }

  return (
    <Stack
      spacing={0}
      key={navItem.name}
      {...(navItem.path
        ? {
            component: MarketingLink,
            shouldUseNavLink: true,
            to: navItem.path,
            end: navItem.path === EAdminPortalClientPath.admin,
          }
        : { component: 'div' })}
      className={[
        navItem.children?.length ? 'collapsible' : '',
        navItem.name === ENavItemNames.postConvoSettings &&
        (pathname === EClientPath.settingsPostConvoSettings ||
          pathname === EClientPath.settingsAnalysisSettingsV2)
          ? 'active'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleCollapseClick}
      sx={{
        textDecoration: 'none',
        textAlign: 'center',
        color: theme.palette.menuFont,

        '& .MuiButtonBase-root': {
          marginBottom: rem(8),
          px: rem(10),

          '&:hover svg': isPartner
            ? darken(theme.palette.menuIcon, 0.4)
            : theme.palette.common.white,
        },
        '& .MuiListItemButton-root': {
          borderTopLeftRadius: '1rem',
          borderBottomLeftRadius: '1rem',
          py: 0,
        },
        '& .MuiListItemText-root': {
          my: 0,
        },
        '& .MuiListItemText-primary': {
          ml: 1,
          textAlign: 'left',
          whiteSpace: 'nowrap',
        },
        '& .MuiTypography-root': {
          ml: rem(12),
          fontWeight: '300',
          letterSpacing: rem(2),
          fontSize: rem(14),
          lineHeight: rem(34),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '&.active:not(.portal-config-override)': {
          '&.collapsible, & .MuiListItemText-primary': {
            fontWeight: 'bold',
            '& svg': {
              fill: isPartner
                ? darken(theme.palette.menuFont, 0.4)
                : theme.palette.menuFont,
            },
            '& .MuiListItemButton-root.active .MuiListItemText-primary': {
              fontWeight: 'bold',
            },
          },
        },
        '& .MuiCollapse-wrapperInner .MuiButtonBase-root.MuiListItemButton-root':
          {
            marginLeft: rem(17),
            marginBottom: rem(4),
            '&:last-child': {
              marginBottom: rem(22),
            },
            '& .MuiTypography-root': {
              pl: rem(14),
              fontWeight: '300',
              fontSize: rem(15),
              letterSpacing: rem(1.7),
            },
            '.MuiListItemText-root.active > .MuiTypography-root': {
              fontWeight: 700,
            },
          },
      }}
    >
      <ListItemButton
        onClick={() => {
          if (!navItem.children?.length) {
            close()
          }
        }}
        ref={collapseTriggerRef}
        className={
          navItem.children?.some((child) => isChildNavActive(child))
            ? 'active'
            : ''
        }
        sx={{
          '&.active': {
            '.MuiTypography-root': {
              fontWeight: 'bold',
            },
            '& svg': {
              fill: isPartner
                ? darken(theme.palette.menuFont, 0.4)
                : theme.palette.menuFont,
            },
            '& > svg': {
              width: rem(23),
              height: rem(23),
              fill: isPartner
                ? darken(theme.palette.menuFont, 0.4)
                : theme.palette.menuFont,
              marginTop: rem(-2),
            },
          },

          '&:hover svg': {
            fill: isPartner
              ? darken(theme.palette.menuFont, 0.4)
              : theme.palette.menuFont,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 'auto',
            color: theme.palette.menuFont,
            flexGrow: 0,
            width: rem(19),
            overflow: 'hidden',
            marginTop: rem(-2),
          }}
        >
          {navItem.icon}
        </ListItemIcon>
        <ListItemText
          primary={navItem.name}
          secondary={navItem.secondaryText}
          primaryTypographyProps={{
            sx: { display: 'inline-flex' },
          }}
          secondaryTypographyProps={{
            component: 'span',
            sx: {
              color: theme.palette.primary2,
              textTransform: 'lowercase',
              display: 'inline-flex',
              '&.MuiTypography-root': {
                fontSize: rem(12),
              },
            },
          }}
        />
        {navItem.children?.length && getCollapsedState(navItem) && (
          <ExpandLessRounded />
        )}
        {navItem.children?.length && !getCollapsedState(navItem) && (
          <ExpandMoreRounded />
        )}
      </ListItemButton>
      {navItem.children?.length && (
        <Collapse in={getCollapsedState(navItem)} timeout="auto" unmountOnExit>
          <List disablePadding>
            {navItem.children?.map((child) => {
              if ('path' in child) {
                const hasSubMenus = !!child.children && !!child.children.length
                const isActiveBySubMenu =
                  hasSubMenus &&
                  child.children!.some(
                    (subChild) =>
                      subChild.path === pathname ||
                      (subChild.path &&
                        pathname.startsWith(`${subChild.path}/`)),
                  )
                const isActiveByPath =
                  !!child.path &&
                  (child.path === pathname ||
                    pathname.startsWith(`${child.path}/`))

                const { openInNewTab } = child

                const newTabProps = openInNewTab
                  ? { target: '_blank', rel: 'noreferrer' }
                  : {}

                const childLink = (
                  <ListItemButton
                    component={MarketingLink}
                    shouldUseNavLink
                    to={child.path || ''}
                    sx={{
                      ml: 4,
                      color: theme.palette.menuFont,
                    }}
                    onClick={close}
                    key={child.name}
                    {...newTabProps}
                  >
                    <ListItemText
                      className={
                        isActiveBySubMenu || isActiveByPath ? 'active' : ''
                      }
                      primary={
                        <>
                          {child.name}
                          {openInNewTab && (
                            <OpenInNew
                              sx={{ fontSize: rem(12), opacity: 0.8 }}
                            />
                          )}
                        </>
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        '> .MuiListItemText-primary': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: rem(6),
                        },
                        '> .MuiListItemText-secondary': {
                          display: 'flex',
                          alignItems: 'center',
                          color: 'inherit',
                          '&.MuiTypography-root': {
                            marginLeft: 0,
                          },
                          '> .MuiButtonBase-root': {
                            marginBottom: 0,
                            color: 'inherit',
                            padding: 0,
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                )

                if (!hasSubMenus) return childLink

                // The sub-menu sits after its parent link, not inside it: a link inside a link is invalid HTML.
                return (
                  <Box
                    key={child.name}
                    sx={{ '&:last-child': { marginBottom: rem(18) } }}
                  >
                    {childLink}
                    {hasSubMenus && (
                      <List
                        sx={{
                          ml: 4,
                          pl: rem(10),
                          '@media (min-width: 0px)': {
                            '&&& > .MuiListItemButton-root:last-child': {
                              marginBottom: 0,
                            },
                          },
                        }}
                      >
                        {child.children!.map((subChild) => (
                          <ListItemButton
                            component={MarketingLink}
                            shouldUseNavLink
                            key={subChild.path}
                            to={subChild.path || ''}
                            onClick={close}
                          >
                            <ListItemText
                              primary={subChild.name}
                              className={
                                subChild.path === pathname ? 'active' : ''
                              }
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    )}
                  </Box>
                )
              }

              return (
                <ListItemButton
                  className={
                    pathByFriendlyStepName[
                      child.name as ESettingsStepFriendlyNames
                    ] === pathname
                      ? 'active'
                      : ''
                  }
                  sx={{
                    ml: 4,
                    color: theme.palette.menuFont,
                  }}
                  key={child.name}
                >
                  <ListItemText primary={child.name} />
                </ListItemButton>
              )
            })}
          </List>
        </Collapse>
      )}
    </Stack>
  )
}

export default NavItem
